import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createActivityTicket,
  deleteActivityTicket,
  updateActivityTicket,
} from "../../activityService";
import ActivityInclusion from "../../AddActivityTabs/ActivityInclusion";
import ActivityExclusion from "../../AddActivityTabs/ActivityExclusion";
import ActivityItinerary from "../../AddActivityTabs/ActivityItinerary";
import ConfirmDialog from "../../../../../dialog/Dialog";
import Toster from "../../../../../common/Toster";

const TABS = [
  { id: "basic", label: "Basic" },
  { id: "inclusions", label: "Inclusions" },
  { id: "exclusions", label: "Exclusions" },
  { id: "itinerary", label: "Itinerary" },
];

const parseCreatedTicketId = (res) => {
  const payload = res?.data;
  if (Array.isArray(payload) && payload[0]?.id) return payload[0].id;
  if (payload?.id) return payload.id;
  if (Array.isArray(res) && res[0]?.id) return res[0].id;
  return null;
};

const TicketTypeDrawer = ({ open, onClose, activityId, ticket, isNew }) => {
  const [activeTab, setActiveTab] = useState("basic");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [ticketId, setTicketId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showToster, setShowToster] = useState(false);
  const [tosterMessage, setTosterMessage] = useState("");
  const [tosterType, setTosterType] = useState("success");
  const queryClient = useQueryClient();
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setActiveTab("basic");
      setName(ticket?.name || "");
      setDescription(ticket?.description || "");
      setTicketId(ticket?.id || null);
    }
    wasOpenRef.current = open;
  }, [open, ticket?.id, ticket?.name, ticket?.description]);

  const createMutation = useMutation({
    mutationFn: (data) => createActivityTicket(activityId, data),
    onSuccess: (res) => {
      const id = parseCreatedTicketId(res);
      if (id) setTicketId(id);
      queryClient.invalidateQueries(["activityTickets", activityId]);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateActivityTicket(activityId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["activityTickets", activityId]);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteActivityTicket(activityId, id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries(["activityTickets", activityId]);
      queryClient.invalidateQueries(["activityTicketTypePrices", activityId]);
      queryClient.invalidateQueries(["activityInclusions", activityId, id]);
      queryClient.invalidateQueries(["activityExclusions", activityId, id]);
      queryClient.invalidateQueries(["activityItinerary", activityId, id]);
      setShowDeleteConfirm(false);
      onClose();
    },
    onError: (error) => {
      setTosterMessage(
        error?.response?.data?.message || error?.message || "Failed to delete ticket type"
      );
      setTosterType("error");
      setShowToster(true);
    },
  });

  const handleSaveBasic = async () => {
    if (!name.trim()) return;

    if (ticketId) {
      await updateMutation.mutateAsync({
        id: ticketId,
        data: { name: name.trim(), description: description.trim() },
      });
    } else {
      const res = await createMutation.mutateAsync({
        tickets: [{ name: name.trim(), description: description.trim(), status: true }],
      });
      const id = parseCreatedTicketId(res);
      if (id) setTicketId(id);
    }

    if (isNew) {
      setActiveTab("inclusions");
    }
  };

  const effectiveTicketId = ticketId || ticket?.id;
  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isEditing = Boolean(effectiveTicketId) && !isNew;

  const handleFinish = () => {
    queryClient.invalidateQueries(["activityTickets", activityId]);
    queryClient.invalidateQueries(["activityTicketTypePrices", activityId]);
    onClose();
  };

  const footerAction = (() => {
    if (!effectiveTicketId || activeTab === "basic") return null;

    if (isEditing) {
      return { label: "Done", onClick: handleFinish };
    }

    if (!isNew) return null;

    if (activeTab === "inclusions") {
      return { label: "Continue to Exclusions", onClick: () => setActiveTab("exclusions") };
    }
    if (activeTab === "exclusions") {
      return { label: "Continue to Itinerary", onClick: () => setActiveTab("itinerary") };
    }
    if (activeTab === "itinerary") {
      return { label: "Done", onClick: handleFinish };
    }
    return null;
  })();

  return (
    <>
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-0 sm:pl-10">
            <DialogPanel
              transition
              className="pointer-events-auto w-screen max-w-xl transform transition duration-300 ease-in-out data-[closed]:translate-x-full"
            >
              <div className="flex h-full flex-col bg-white shadow-2xl">
                <div className="flex items-center justify-between border-b border-gray-200 px-4 py-4 sm:px-6">
                  <DialogTitle className="text-lg font-bold text-gray-900">
                    {isNew && !effectiveTicketId
                      ? "Add Ticket Type"
                      : `Edit: ${name || ticket?.name || "Ticket"}`}
                  </DialogTitle>
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
                    aria-label="Close"
                  >
                    <i className="fi fi-rr-cross text-sm" />
                  </button>
                </div>

                <div className="flex gap-1 overflow-x-auto border-b border-gray-100 px-4 py-2">
                  {TABS.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      disabled={tab.id !== "basic" && !effectiveTicketId}
                      onClick={() => setActiveTab(tab.id)}
                      className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                        activeTab === tab.id
                          ? "bg-primary text-white"
                          : "text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6">
                  {activeTab === "basic" && (
                    <div className="space-y-4">
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          Ticket name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="e.g. Shared, Private, VIP"
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Activity type (Shared / Private / Exclusive) is set via ticket name.
                        </p>
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          Description
                        </label>
                        <textarea
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          rows={3}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none"
                          placeholder="Brief description of this ticket type"
                        />
                      </div>
                      <div className="flex gap-2 pt-2">
                        <button
                          type="button"
                          onClick={handleSaveBasic}
                          disabled={!name.trim() || isSaving}
                          className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                        >
                          {isSaving
                            ? "Saving..."
                            : isEditing
                              ? "Save changes"
                              : "Save ticket"}
                        </button>
                        {effectiveTicketId && !isNew && (
                          <button
                            type="button"
                            onClick={() => setShowDeleteConfirm(true)}
                            disabled={deleteMutation.isPending}
                            className="rounded-xl border border-red-200 px-4 py-2 text-sm font-medium text-red-600 disabled:opacity-50"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {activeTab === "inclusions" && effectiveTicketId && (
                    <SingleTicketInclusions
                      activityId={activityId}
                      ticketId={effectiveTicketId}
                      ticketName={name}
                    />
                  )}
                  {activeTab === "exclusions" && effectiveTicketId && (
                    <SingleTicketExclusions
                      activityId={activityId}
                      ticketId={effectiveTicketId}
                      ticketName={name}
                    />
                  )}
                  {activeTab === "itinerary" && effectiveTicketId && (
                    <SingleTicketItinerary
                      activityId={activityId}
                      ticketId={effectiveTicketId}
                      ticketName={name}
                    />
                  )}
                </div>

                {footerAction && effectiveTicketId && (
                  <div className="border-t border-gray-200 bg-white/95 px-4 py-4 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] backdrop-blur-sm sm:px-6">
                    <button
                      type="button"
                      onClick={footerAction.onClick}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 text-sm font-bold text-white shadow-md shadow-primary/25 transition hover:bg-primary/90 hover:shadow-lg active:scale-[0.99]"
                    >
                      <i
                        className={`fi relative top-px text-sm ${
                          footerAction.label === "Done" ? "fi-rr-check" : "fi-rr-angle-right"
                        }`}
                      />
                      {footerAction.label}
                    </button>
                  </div>
                )}
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>

    <ConfirmDialog
      open={showDeleteConfirm}
      onClose={() => setShowDeleteConfirm(false)}
      title={
        <div className="flex items-center gap-2">
          <i className="fi fi-rr-trash text-base" />
          Delete ticket type
        </div>
      }
      primaryButtonText="Delete"
      primaryButtonAction={() => effectiveTicketId && deleteMutation.mutate(effectiveTicketId)}
      closeButtonText="Cancel"
      closeButtonAction={() => setShowDeleteConfirm(false)}
      isLoading={deleteMutation.isPending}
      maxWidth="md"
    >
      <div className="mt-2">
        <p className="text-sm text-gray-500">
          Are you sure you want to delete <strong>{name || "this ticket type"}</strong>?
          This will also remove its pricing, inclusions, exclusions, and itinerary.
        </p>
      </div>
    </ConfirmDialog>

    {showToster && (
      <Toster message={tosterMessage} type={tosterType} onClose={() => setShowToster(false)} />
    )}
  </>
  );
};

const SingleTicketInclusions = ({ activityId, ticketId, ticketName }) => (
  <ActivityInclusion
    activityId={activityId}
    singleTicketId={ticketId}
    singleTicketName={ticketName}
  />
);

const SingleTicketExclusions = ({ activityId, ticketId, ticketName }) => (
  <ActivityExclusion
    activityId={activityId}
    singleTicketId={ticketId}
    singleTicketName={ticketName}
  />
);

const SingleTicketItinerary = ({ activityId, ticketId, ticketName }) => (
  <ActivityItinerary
    activityId={activityId}
    singleTicketId={ticketId}
    singleTicketName={ticketName}
  />
);

export default TicketTypeDrawer;
